import React, {useEffect, useState} from 'react';
import axios from "axios";
import {toast} from 'react-toastify';
import {Link} from 'react-router-dom';
import {
	Check,
	CheckCircle,
	Envelope,
	FloppyDisk,
	MagnifyingGlass,
	PaperPlaneTilt,
	Pause,
	PencilSimple,
	Play,
	Plus,
	Users,
	X,
} from '@phosphor-icons/react';
import './Campaign.css';
import Modal from '../MediaUpload/Modal';
import AddLeads from './AddLeads';
import {getBaseUrlNodeServer} from "../../config";
import {renderMjml} from "../../lib/mjmlPreview";
import {auth} from "../../firebase";

// Hardcoded — the new REST API lives at app.neugence.com regardless of the
// legacy applyjobs.ai:8080 Node server still used for /savecampaign etc.
const NEUGENCE_API_BASE = 'https://app.neugence.com';

async function bearerHeaders() {
	// Firebase ID token. Mirrors the pattern in Settings/ApiKeys.jsx so a
	// single auth model covers all v1 REST endpoints.
	const user = auth && auth.currentUser;
	if (!user) throw new Error('Not signed in');
	const token = await user.getIdToken();
	return {Authorization: `Bearer ${token}`};
}

/* ------------------------------------------------------------------ */
/* Status helpers — preserve backend values ('Draft' | 'Paused' |     */
/* 'Running'), display friendlier labels.                             */
/* ------------------------------------------------------------------ */
const STATUS_STYLE = {
	Draft: 'bg-slate-100 text-slate-700',
	Paused: 'bg-amber-100 text-amber-800',
	Running: 'bg-emerald-100 text-emerald-800',
	Live: 'bg-emerald-100 text-emerald-800',
	Done: 'bg-slate-200 text-slate-800',
	Ready: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const StatusBadge = ({status}) => {
	const cls = STATUS_STYLE[status] || 'bg-slate-100 text-slate-700';
	return (
		<span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
			{status || 'Draft'}
		</span>
	);
};

/* ------------------------------------------------------------------ */
/* Template picker modal — preview-first, scrollable card grid with   */
/* live MJML→HTML thumbnails, search, and "Create new template" CTA.  */
/* HTML previews are read from templateIdToHtmlDict (already fetched  */
/* once on mount) so opening/closing the modal is free.               */
/* ------------------------------------------------------------------ */
const PreviewThumbnail = ({html, templateId}) => {
	if (!html) {
		return (
			<div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
				<span className="animate-pulse">Loading preview…</span>
			</div>
		);
	}
	return (
		<div
			style={{
				position: 'absolute',
				inset: 0,
				overflow: 'hidden',
				pointerEvents: 'none',
			}}>
			<iframe
				title={`Preview ${templateId}`}
				srcDoc={html}
				sandbox=""
				style={{
					width: '250%',
					height: '250%',
					border: 'none',
					transform: 'scale(0.4)',
					transformOrigin: 'top left',
					pointerEvents: 'none',
					background: '#fff',
				}}/>
		</div>
	);
};

const TemplatePickerModal = ({
	isOpen,
	onClose,
	templates,
	previews,
	currentSelectedId,
	onConfirm,
	onCreateNew,
	search,
	setSearch,
}) => {
	const [pendingId, setPendingId] = useState('');

	/* Reset tentative selection to whatever the campaign already has */
	/* whenever the modal (re-)opens.                                 */
	useEffect(() => {
		if (isOpen) setPendingId(currentSelectedId || '');
	}, [isOpen, currentSelectedId]);

	useEffect(() => {
		if (!isOpen) return undefined;
		const onEsc = (e) => {
			if (e.keyCode === 27) onClose();
		};
		document.addEventListener('keydown', onEsc);
		return () => document.removeEventListener('keydown', onEsc);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const q = (search || '').trim().toLowerCase();
	const filtered = !q
		? templates
		: templates.filter(t => {
			const subj = (t.subject || '').toLowerCase();
			const cats = Array.isArray(t.selectedCategories)
				? t.selectedCategories.join(' ').toLowerCase()
				: '';
			const id = (t._id || '').toLowerCase();
			return subj.includes(q) || cats.includes(q) || id.includes(q);
		});

	const handleConfirm = () => {
		if (!pendingId) return;
		onConfirm(pendingId);
		onClose();
	};

	const renderEmpty = () => {
		const noTemplatesYet = templates.length === 0;
		return (
			<div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center">
				<div className="mx-auto w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
					<Envelope size={24} weight="duotone" color="#10B981"/>
				</div>
				<h3 className="text-base font-semibold text-slate-900 mb-1">
					{noTemplatesYet ? 'No templates yet' : 'No templates match'}
				</h3>
				<p className="text-sm text-slate-600 mb-5">
					{noTemplatesYet
						? 'Create your first template to use it in this campaign.'
						: 'Try a different search term, or spin up a brand new template.'}
				</p>
				<button
					type="button"
					onClick={onCreateNew}
					className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-full px-4 py-2 text-sm font-medium transition-colors">
					<Plus size={14} weight="bold"/> Create new template
				</button>
			</div>
		);
	};

	return (
		<div
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4 py-6">
			<div
				role="dialog"
				aria-modal="true"
				className="rounded-2xl shadow-2xl bg-white max-w-5xl w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">

				{/* ---------- Header ---------- */}
				<div className="flex flex-col md:flex-row md:items-center gap-3 px-6 py-4 border-b border-slate-100">
					<div className="flex items-center justify-between md:flex-shrink-0">
						<h3 className="text-lg font-semibold text-slate-900">Choose a template</h3>
						<button
							type="button"
							onClick={onClose}
							aria-label="Close"
							className="md:hidden rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
							<X size={18} weight="bold"/>
						</button>
					</div>
					<div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 md:justify-end">
						<div className="relative sm:flex-1 sm:max-w-sm md:ml-auto">
							<MagnifyingGlass
								size={16}
								weight="bold"
								color="#94A3B8"
								style={{position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)'}}/>
							<input
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search by subject or category…"
								className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"/>
						</div>
						<button
							type="button"
							onClick={onCreateNew}
							className="inline-flex items-center justify-center gap-2 bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap">
							<Plus size={14} weight="bold"/> Create new template
						</button>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="hidden md:inline-flex rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
						<X size={18} weight="bold"/>
					</button>
				</div>

				{/* ---------- Body ---------- */}
				<div className="flex-1 overflow-y-auto p-6 bg-slate-50/40">
					{filtered.length === 0 ? renderEmpty() : (
						<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{filtered.map((tpl) => {
								const id = tpl._id;
								const html = previews[id];
								const isSelected = id === pendingId;
								const cats = Array.isArray(tpl.selectedCategories) ? tpl.selectedCategories : [];
								return (
									<button
										key={id}
										type="button"
										onClick={() => setPendingId(id)}
										className={`text-left rounded-2xl border bg-white overflow-hidden transition cursor-pointer ${
											isSelected
												? 'ring-2 ring-emerald-500 border-emerald-300 shadow-lg'
												: 'border-slate-200 hover:border-emerald-300 hover:shadow-lg'
										}`}>
										{/* 16:9 thumbnail */}
										<div className="relative bg-slate-100" style={{paddingTop: '56.25%'}}>
											<PreviewThumbnail html={html} templateId={id}/>
											{isSelected && (
												<div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
													<Check size={16} weight="bold" color="#fff"/>
												</div>
											)}
										</div>
										{/* Meta */}
										<div className="p-4 border-t border-slate-100">
											<div className="text-sm font-semibold text-slate-900 line-clamp-2 mb-1.5 min-h-[2.5rem]">
												{tpl.subject ? tpl.subject : 'Untitled template'}
											</div>
											{cats.length > 0 && (
												<div className="flex flex-wrap gap-1.5">
													{cats.slice(0, 3).map((c, i) => (
														<span
															key={`${id}-cat-${i}`}
															className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-medium">
															{c}
														</span>
													))}
													{cats.length > 3 && (
														<span className="text-[11px] text-slate-400 self-center">
															+{cats.length - 3}
														</span>
													)}
												</div>
											)}
										</div>
									</button>
								);
							})}
						</div>
					)}
				</div>

				{/* ---------- Sticky footer ---------- */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-white">
					<div className="text-sm text-slate-600">
						{pendingId ? '1 selected' : 'Nothing selected'}
					</div>
					<div className="flex items-center justify-end gap-3">
						<button
							type="button"
							onClick={onClose}
							className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-2.5 font-medium transition-colors">
							Cancel
						</button>
						<button
							type="button"
							onClick={handleConfirm}
							disabled={!pendingId}
							className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-2.5 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
							Use selected template
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

/* ------------------------------------------------------------------ */
/* EditCampaign                                                       */
/* ------------------------------------------------------------------ */
const EditCampaign = ({currentCampaignFromParent}) => {
	const [currentCampaign, setcurrentCampaign] = useState(currentCampaignFromParent);
	const [isLeadsModalOpen, setIsLeadsModalOpen] = useState(false);
	const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
	const [selectedContacts, setParentSelectedContacts] = useState([]);
	const [usertemplates, setusertemplates] = useState([]);
	const [selectedItem, setselectedItem] = useState('');
	const [templateIdToHtmlDict, settemplateIdToHtmlDict] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [templateSearch, setTemplateSearch] = useState('');
	const [isSendModalOpen, setIsSendModalOpen] = useState(false);
	const [isSending, setIsSending] = useState(false);

	const verifiedemail = sessionStorage.getItem("verifiedemail");

	/* ---------------------- Data fetching --------------------------- */
	useEffect(() => {
		window.scrollTo(0, 0);

		async function getCampaigns() {
			if (currentCampaignFromParent == null || currentCampaignFromParent === undefined || currentCampaignFromParent === '') {
				return;
			}
			axios.defaults.baseURL = getBaseUrlNodeServer();
			axios.post('getonecampaign', {
				'verifiedemail': verifiedemail,
				'campaignname': currentCampaign.campaignname,
			}).then(response => {
				const usercampaignArr = response.data;
				setcurrentCampaign(usercampaignArr[0]);
				setselectedItem(usercampaignArr[0].seletectTemplateId);
				setParentSelectedContacts(usercampaignArr[0].targetLeads);
			}).catch(err => console.log(err));

			const response = await axios.post('getusersavedtemplates', {'verifiedemail': verifiedemail});
			const userTpl = response.data;

			// Client-side MJML → HTML via mjml-browser (memoized). Was
			// N HTTP round-trips to /mjml2html serialized in Promise.all;
			// now sync, no network, <50ms each.
			const finalResult = {};
			for (const template of userTpl) {
				finalResult[template._id] = {
					subject: template.subject,
					html: renderMjml(template.content || ''),
				};
			}

			settemplateIdToHtmlDict(finalResult);
			setusertemplates(userTpl);
			setIsLoading(false);
		}

		getCampaigns();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* ---------------------- API helpers ----------------------------- */
	function getAndSetCampaign() {
		axios.defaults.baseURL = getBaseUrlNodeServer();
		axios.post('getonecampaign', {
			'verifiedemail': verifiedemail,
			'campaignname': currentCampaign.campaignname,
		}).then(response => {
			const usercampaignArr = response.data;
			setcurrentCampaign(usercampaignArr[0]);
		}).catch(err => console.log(err));
	}

	function saveCampaign() {
		const targetLeads = selectedContacts;
		const seletectTemplateId = selectedItem;
		const selectedTpl = templateIdToHtmlDict[selectedItem];
		const selectedItemHtml = selectedTpl ? selectedTpl.html : '';

		setIsSaving(true);
		axios.defaults.baseURL = getBaseUrlNodeServer();
		axios.post('savecampaign', {
			'verifiedemail': verifiedemail,
			'campaignname': currentCampaign.campaignname,
			'targetLeads': targetLeads,
			'seletectTemplateId': seletectTemplateId,
			'selectedItemHtml': selectedItemHtml,
		}).then(() => {
			toast.success('Draft saved');
			getAndSetCampaign();
		}).catch(err => {
			console.log(err);
			toast.error('Could not save draft');
		}).finally(() => setIsSaving(false));
	}

	function setCampaignStatus(status) {
		axios.defaults.baseURL = getBaseUrlNodeServer();
		axios.post('setcampaignstatus', {
			'verifiedemail': verifiedemail,
			'campaignname': currentCampaign.campaignname,
			'targetLeads': selectedContacts,
			'seletectTemplateId': selectedItem,
			'status': status,
		}).then(() => getAndSetCampaign())
			.catch(err => console.log(err));
	}

	function clickRun() {
		setCampaignStatus('Running');
		toast.success('Running now — sit tight!');
	}

	function clickPause() {
		setCampaignStatus('Paused');
		toast.success('Campaign paused — no more emails will be sent.');
	}

	/* ---------------------- Real send (new REST API) --------------- */
	// POST app.neugence.com/api/v1/campaigns/<id>/send {test_only:false} —
	// iterates recipient_list server-side (skips suppressed via List-Unsubscribe).
	async function sendCampaignReal() {
		const campaignId = currentCampaign && currentCampaign._id;
		if (!campaignId) {
			toast.error('Campaign id missing — save the draft first.');
			return;
		}
		setIsSending(true);
		try {
			const headers = await bearerHeaders();
			const res = await axios.post(
				`${NEUGENCE_API_BASE}/api/v1/campaigns/${campaignId}/send`,
				{test_only: false},
				{headers},
			);
			const data = (res && res.data) || {};
			const sent = typeof data.sent === 'number' ? data.sent : 0;
			const skipped = typeof data.skipped_suppressed === 'number' ? data.skipped_suppressed : 0;
			const failed = typeof data.failed === 'number' ? data.failed : 0;
			toast.success(`Sent to ${sent} / Skipped ${skipped} / Failed ${failed}`);
			setIsSendModalOpen(false);
			// Refresh campaign so the Status badge flips Running → Done.
			getAndSetCampaign();
		} catch (err) {
			console.log('send campaign error', err);
			toast.error('Could not send campaign');
		} finally {
			setIsSending(false);
		}
	}

	/* ---------------------- Derived state --------------------------- */
	const selectedTemplate = templateIdToHtmlDict[selectedItem];
	const recipientCount = selectedContacts ? selectedContacts.length : 0;
	const hasTemplate = !!(selectedItem && selectedTemplate);
	const canSend = hasTemplate && recipientCount > 0;
	const status = currentCampaign && currentCampaign.Status ? currentCampaign.Status : 'Draft';
	const isRunning = status === 'Running' || status === 'Live';

	/* Flatten templateIdToHtmlDict into {id: html} for the picker      */
	/* (the modal renders previews from this lookup table).             */
	const previewHtmlById = {};
	Object.keys(templateIdToHtmlDict).forEach(id => {
		const tpl = templateIdToHtmlDict[id];
		previewHtmlById[id] = tpl && tpl.html ? tpl.html : '';
	});

	/* ---------------------- Empty state ----------------------------- */
	if (!(currentCampaign && currentCampaign.campaignname && currentCampaign.campaignname.length > 0)) {
		return (
			<div className="min-h-screen bg-white">
				<div className="max-w-6xl mx-auto px-6 py-24">
					<div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-12 text-center">
						<div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
							<Envelope size={26} weight="duotone" color="#10B981"/>
						</div>
						<h2 className="text-xl font-semibold text-slate-900 mb-2">No campaign selected</h2>
						<p className="text-slate-600">Head to <span className="font-medium text-slate-900">Campaigns</span> and pick one to manage.</p>
					</div>
				</div>
			</div>
		);
	}

	/* ---------------------- Render ---------------------------------- */
	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 pt-10 pb-32">

				{/* ---------- Top bar ---------- */}
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div className="flex items-center gap-3">
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900">
							{currentCampaign.campaignname}
						</h1>
						<StatusBadge status={status}/>
					</div>
					<div className="flex items-center gap-3">
						{isRunning ? (
							<button
								type="button"
								onClick={clickPause}
								className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-sm font-medium transition-colors">
								<Pause size={16} weight="bold"/> Pause
							</button>
						) : (
							<button
								type="button"
								onClick={clickRun}
								disabled={!canSend}
								className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
								<Play size={16} weight="bold"/> Resume
							</button>
						)}
					</div>
				</div>

				{isLoading && (
					<div className="rounded-2xl border border-slate-100 bg-slate-50/60 px-5 py-3 mb-6 text-sm text-slate-600">
						Loading templates and contacts…
					</div>
				)}

				{/* ---------- Two-column layout ---------- */}
				<div className="grid lg:grid-cols-3 gap-8">

					{/* -------- Left: template preview -------- */}
					<div className="lg:col-span-2">
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
							<div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
								<div>
									<h2 className="text-sm font-semibold text-slate-900">Email preview</h2>
									<p className="text-xs text-slate-500 mt-0.5">
										{selectedTemplate && selectedTemplate.subject
											? <>Subject: <span className="text-slate-700">{selectedTemplate.subject}</span></>
											: 'No template selected yet.'}
									</p>
								</div>
								<div className="flex items-center gap-2">
									{hasTemplate && (
										<Link
											to={`/editor/${currentCampaign.seletectTemplateId}`}
											className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-sm font-medium transition-colors">
											<PencilSimple size={14} weight="bold"/> Edit template
										</Link>
									)}
									<button
										type="button"
										onClick={() => setIsTemplateModalOpen(true)}
										className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-sm transition-colors">
										{hasTemplate ? 'Change template' : 'Choose template'}
									</button>
								</div>
							</div>

							<div className="bg-slate-50 p-6">
								{hasTemplate ? (
									<iframe
										title="Email preview"
										srcDoc={selectedTemplate.html}
										className="w-full bg-white rounded-xl border border-slate-200"
										style={{height: '720px'}}
									/>
								) : (
									<div className="rounded-xl border border-dashed border-slate-300 bg-white py-24 text-center">
										<div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-4">
											<Envelope size={24} weight="duotone" color="#10B981"/>
										</div>
										<h3 className="text-base font-semibold text-slate-900 mb-1">Pick a template to preview</h3>
										<p className="text-sm text-slate-600 mb-5">Your saved designs will render here exactly as recipients see them.</p>
										<button
											type="button"
											onClick={() => setIsTemplateModalOpen(true)}
											className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-sm transition-colors">
											Choose template
										</button>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* -------- Right: sidebar -------- */}
					<div className="lg:col-span-1 flex flex-col gap-6">

						{/* Recipients */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
									<Users size={18} weight="duotone" color="#10B981"/>
								</div>
								<div>
									<h3 className="text-sm font-semibold text-slate-900">Recipients</h3>
									<p className="text-xs text-slate-500">Who receives this campaign</p>
								</div>
							</div>
							<div className="flex items-baseline gap-2 mb-4">
								<span className="text-3xl font-semibold text-slate-900">{recipientCount}</span>
								<span className="text-sm text-slate-600">contacts selected</span>
							</div>
							<button
								type="button"
								onClick={() => setIsLeadsModalOpen(true)}
								className="w-full rounded-full px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-sm font-medium transition-colors">
								{recipientCount > 0 ? 'Edit recipients' : 'Pick contacts'}
							</button>
						</div>

						{/* Subject */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
									<Envelope size={18} weight="duotone" color="#10B981"/>
								</div>
								<div>
									<h3 className="text-sm font-semibold text-slate-900">Subject line</h3>
									<p className="text-xs text-slate-500">Set on the template</p>
								</div>
							</div>
							<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 min-h-[48px] flex items-center">
								{selectedTemplate && selectedTemplate.subject
									? selectedTemplate.subject
									: <span className="text-slate-400">Choose a template to set subject</span>}
							</div>
						</div>

						{/* Send checklist */}
						<div className="rounded-2xl border border-emerald-200 bg-emerald-50 shadow-sm p-6">
							<h3 className="text-sm font-semibold text-emerald-900 mb-3">Ready to send?</h3>
							<ul className="space-y-2 text-sm">
								<li className="flex items-center gap-2 text-slate-700">
									<CheckCircle
										size={18}
										weight={hasTemplate ? 'fill' : 'regular'}
										color={hasTemplate ? '#10B981' : '#94A3B8'}/>
									Template selected
								</li>
								<li className="flex items-center gap-2 text-slate-700">
									<CheckCircle
										size={18}
										weight={recipientCount > 0 ? 'fill' : 'regular'}
										color={recipientCount > 0 ? '#10B981' : '#94A3B8'}/>
									At least one recipient
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* ---------- Sticky bottom action bar ---------- */}
			<div className="fixed bottom-0 inset-x-0 border-t border-slate-100 bg-white/90 backdrop-blur-sm">
				<div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
					<div className="text-xs text-slate-500">
						{isSaving
							? 'Saving…'
							: canSend
								? 'Looking good — ready to send.'
								: 'Pick a template and at least one recipient to send.'}
					</div>
					<div className="flex items-center gap-3">
						<button
							type="button"
							onClick={saveCampaign}
							disabled={isSaving}
							className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-sm font-medium transition-colors disabled:opacity-50">
							<FloppyDisk size={16} weight="bold"/> Save draft
						</button>
						<button
							type="button"
							onClick={() => setIsSendModalOpen(true)}
							disabled={!canSend || isRunning}
							title={!hasTemplate
								? 'Choose a template first'
								: recipientCount === 0
									? 'Pick recipients first'
									: ''}
							className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
							<PaperPlaneTilt size={16} weight="bold"/>
							{isRunning ? 'Running' : 'Send campaign'}
						</button>
					</div>
				</div>
			</div>

			{/* ---------- Leads modal (shared MediaUpload/Modal) ---------- */}
			<Modal isOpen={isLeadsModalOpen} onClose={() => setIsLeadsModalOpen(false)}>
				<AddLeads
					currentCampaign={currentCampaign}
					setParentSelectedContacts={setParentSelectedContacts}
					parentSelectedContacts={selectedContacts}
				/>
			</Modal>

			{/* ---------- Template picker modal ---------- */}
			<TemplatePickerModal
				isOpen={isTemplateModalOpen}
				onClose={() => setIsTemplateModalOpen(false)}
				templates={usertemplates}
				previews={previewHtmlById}
				currentSelectedId={selectedItem}
				onConfirm={(id) => setselectedItem(id)}
				onCreateNew={() => window.open('/editor', '_blank')}
				search={templateSearch}
				setSearch={setTemplateSearch}/>

			{/* ---------- Send-campaign confirmation modal ---------- */}
			{isSendModalOpen && (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="send-campaign-title"
					onMouseDown={(e) => {
						if (e.target === e.currentTarget && !isSending) setIsSendModalOpen(false);
					}}
					className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
					<div className="rounded-2xl shadow-2xl bg-white max-w-md w-full overflow-hidden">
						<div className="px-6 pt-6 pb-2 flex items-start justify-between">
							<h3 id="send-campaign-title" className="text-lg font-semibold text-slate-900">
								Send to your list?
							</h3>
							<button
								type="button"
								onClick={() => !isSending && setIsSendModalOpen(false)}
								aria-label="Close"
								className="text-slate-400 hover:text-slate-600 -mt-1 -mr-2 p-2">
								<X size={18} weight="bold"/>
							</button>
						</div>
						<div className="px-6 py-4 text-sm text-slate-700">
							{recipientCount > 0 ? (
								<p>
									This will send to <b>{recipientCount} recipient{recipientCount === 1 ? '' : 's'}</b> via SES.
									Suppressed emails will be skipped.
								</p>
							) : (
								<p className="text-slate-500">
									No recipients selected — pick contacts first, then come back to send.
								</p>
							)}
						</div>
						<div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-3">
							<button
								type="button"
								onClick={() => setIsSendModalOpen(false)}
								disabled={isSending}
								className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-2.5 font-medium transition-colors disabled:opacity-50">
								Cancel
							</button>
							<button
								type="button"
								onClick={sendCampaignReal}
								disabled={isSending || recipientCount === 0}
								className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-2.5 font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
								{isSending && (
									<span
										aria-hidden="true"
										className="inline-block w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin"/>
								)}
								{isSending ? 'Sending…' : 'Send now'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default EditCampaign;
